import { useEffect } from 'react';

import i18n, { setLanguage } from '@/i18n';
import { addRumAction } from '@/lib/datadog-rum';
import { useOrganization } from '@/contexts/organization-context';
import { useAuth } from '@/contexts/auth-context';

import { useProfilePreference, useUpdateProfilePreference } from './use-profile-preference';

// =============================================================================
// Types
// =============================================================================

export type LanguageKey = 'en' | 'fr';

export interface LanguageOption {
  key: LanguageKey;
  /** English label for the option. */
  label: string;
  /** Endonym — the language's name in its own language. */
  nativeLabel: string;
}

export const LANGUAGE_OPTIONS: LanguageOption[] = [
  { key: 'en', label: 'English', nativeLabel: 'English' },
  { key: 'fr', label: 'French', nativeLabel: 'Français' },
];

export const DEFAULT_LANGUAGE: LanguageKey = 'en';

const VALID_LANGUAGES: ReadonlySet<LanguageKey> = new Set(['en', 'fr']);

const LANGUAGE_PREFERENCE_OPTIONS = {
  preferenceKey: 'language',
  queryKeyPrefix: 'language-settings',
  defaultValue: DEFAULT_LANGUAGE,
  validValues: VALID_LANGUAGES,
} as const;

// =============================================================================
// Hooks
// =============================================================================

/**
 * Resolved UI language for the current user.
 * Priority: user's profile preference → org-level setting → DEFAULT_LANGUAGE.
 * Browser detection (i18next LanguageDetector) covers the pre-auth window.
 */
export function useLanguageSettings(organizationId?: string | null): LanguageKey {
  return useProfilePreference<LanguageKey>(organizationId, LANGUAGE_PREFERENCE_OPTIONS);
}

/**
 * Persist the UI language for the current user (writes profiles.preferences).
 * The active i18next language follows reactively via {@link useLanguageSync},
 * keeping a single source of truth.
 */
export function useUpdateLanguageSettings(organizationId: string | null | undefined) {
  return useUpdateProfilePreference<LanguageKey>(organizationId, LANGUAGE_PREFERENCE_OPTIONS);
}

/**
 * Apply the resolved profile/org language preference to the live i18next
 * instance (premortem R6 reconcile after the synchronous localStorage seed).
 * Mount once near the app root. Records `i18n.language_flip` on a real change so
 * a too-frequent flip (the R6 failure mode) is observable in RUM — locale codes
 * only, never PII.
 */
export function useLanguageSync(): void {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const language = useLanguageSettings(currentOrganization?.id ?? null);

  useEffect(() => {
    if (!user || !currentOrganization || i18n.language === language) {
      return;
    }
    const previous = i18n.language;
    // `setLanguage`, not `changeLanguage`: it loads the catalog first, so the
    // switch never lands on an empty one and fall back to English mid-flight
    // (premortem R6).
    setLanguage(language)
      .then(() => {
        addRumAction('i18n.language_flip', { from: previous, to: language });
      })
      .catch(() => {
        /* a failed catalog fetch or language switch leaves the previous language active — non-fatal */
      });
  }, [language, user, currentOrganization]);
}
